import React, { useMemo } from 'react';
import { Card, Col, List, Progress, Row, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  AirflowAnalysisResult,
  AirflowHeatmapCell,
  AirflowRuleFinding,
} from '../types';

const { Paragraph, Title } = Typography;

interface AirflowAssessmentProps {
  airflowAnalysis: AirflowAnalysisResult;
}

const levelLabels: Record<AirflowHeatmapCell['level'], string> = {
  low: '纳气偏弱',
  balanced: '气流均衡',
  strong: '动气偏强',
};

const levelTagColors: Record<AirflowHeatmapCell['level'], string> = {
  low: 'blue',
  balanced: 'green',
  strong: 'red',
};

const formatPercent = (value: number) => Math.round(value * 100);

const getOverallAssessment = (heatmap: AirflowHeatmapCell[]) => {
  if (heatmap.length === 0) {
    return {
      label: '待判断',
      tone: 'neutral',
      score: 0,
      description: '当前没有足够的空间气流数据，暂不能形成可靠判断。',
    };
  }

  const balanced = heatmap.filter((cell) => cell.level === 'balanced').length;
  const strong = heatmap.filter((cell) => cell.level === 'strong').length;
  const low = heatmap.filter((cell) => cell.level === 'low').length;
  const balanceRatio = balanced / heatmap.length;
  const riskRatio = (strong + low) / heatmap.length;
  const score = Math.max(0, Math.min(100, Math.round(balanceRatio * 72 + (1 - riskRatio) * 28)));

  if (score >= 76) {
    return {
      label: '通透性良好',
      tone: 'good',
      score,
      description: '气流分配相对均衡，主要空间能承接入户气，动静关系较稳定。',
    };
  }

  if (score >= 58) {
    return {
      label: '通风路径可用',
      tone: 'balanced',
      score,
      description: '整体可用，但局部空间存在气流偏强或偏弱，需要做小范围缓冲与引导。',
    };
  }

  return {
    label: '通透性待优化',
    tone: 'risk',
    score,
    description: '气流分布不均，可能出现直冲、泄气或局部闭塞，建议优先调整主要动线。',
  };
};

const getGoodPoints = (heatmap: AirflowHeatmapCell[]): string[] => {
  const balancedRooms = heatmap.filter((cell) => cell.level === 'balanced');
  if (balancedRooms.length === 0) {
    return ['当前未发现明显均衡空间，建议优先处理气流过强或过弱区域。'];
  }

  return balancedRooms.slice(0, 3).map((cell) => (
    `${cell.label}气流较均衡，适合作为稳定停留或承接空间。`
  ));
};

const getRiskPoints = (findings: AirflowRuleFinding[], heatmap: AirflowHeatmapCell[]): string[] => {
  if (findings.length > 0) {
    return findings.slice(0, 4).map((finding) => finding.message);
  }

  const riskRooms = heatmap.filter((cell) => cell.level !== 'balanced');
  if (riskRooms.length === 0) {
    return ['未发现明显通风路径关注点。'];
  }

  return riskRooms.slice(0, 4).map((cell) => `${cell.label}${levelLabels[cell.level]}。`);
};

const getSuggestions = (findings: AirflowRuleFinding[]): string[] => {
  const suggestions = findings.map((finding) => finding.suggestion).filter(Boolean);
  const deduped = Array.from(new Set(suggestions));

  if (deduped.length === 0) {
    return ['保持入户、客厅与主要通道整洁通透，避免堆放杂物造成气流阻滞。'];
  }

  return deduped.slice(0, 5);
};

const AirflowAssessment: React.FC<AirflowAssessmentProps> = ({ airflowAnalysis }) => {
  const heatmap = airflowAnalysis.visualization.heatmap;
  const assessment = useMemo(() => getOverallAssessment(heatmap), [heatmap]);
  const sortedRooms = useMemo(
    () => [...heatmap].sort((a, b) => b.flow - a.flow),
    [heatmap],
  );
  const goodPoints = useMemo(() => getGoodPoints(heatmap), [heatmap]);
  const riskPoints = useMemo(
    () => getRiskPoints(airflowAnalysis.findings, heatmap),
    [airflowAnalysis.findings, heatmap],
  );
  const suggestions = useMemo(
    () => getSuggestions(airflowAnalysis.findings),
    [airflowAnalysis.findings],
  );

  return (
    <Card className="result-card airflow-assessment-card" title="气流分布评估" bordered={false}>
      <div className="airflow-assessment">
        <div className={`airflow-verdict airflow-verdict-${assessment.tone}`}>
          <div>
            <span className="section-kicker">Airflow assessment</span>
            <Title level={3}>{assessment.label}</Title>
            <Paragraph>{assessment.description}</Paragraph>
          </div>
          <div className="airflow-verdict-score">
            <strong>{assessment.score}</strong>
            <span>气流分</span>
          </div>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card className="airflow-judgement-panel airflow-judgement-good" bordered={false}>
              <h4>
                <CheckCircleOutlined />
                好的地方
              </h4>
              <List
                size="small"
                dataSource={goodPoints}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card className="airflow-judgement-panel airflow-judgement-risk" bordered={false}>
              <h4>
                <ExclamationCircleOutlined />
                关注点
              </h4>
              <List
                size="small"
                dataSource={riskPoints}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card className="airflow-judgement-panel airflow-judgement-remedy" bordered={false}>
              <h4>
                <FireOutlined />
                调整建议
              </h4>
              <List
                size="small"
                dataSource={suggestions}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            </Card>
          </Col>
        </Row>

        <div className="airflow-room-assessment">
          {sortedRooms.map((cell) => (
            <div key={cell.roomId} className="airflow-room-assessment-row">
              <div>
                <strong>{cell.label}</strong>
                <Tag color={levelTagColors[cell.level]}>{levelLabels[cell.level]}</Tag>
              </div>
              <Progress
                percent={formatPercent(cell.flow)}
                size="small"
                showInfo={false}
                strokeColor={cell.color}
              />
            </div>
          ))}
        </div>

        <div className="airflow-assessment-summary">
          <ThunderboltOutlined />
          <span>{airflowAnalysis.summary}</span>
        </div>
      </div>
    </Card>
  );
};

export default AirflowAssessment;
